import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { X, Wallet } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { fetchAccounts, fetchContacts, createAccount } from '../../redux/crmSlice';

// Shown when a Deal has neither an Account nor a Contact linked — createFromDeal
// requires at least one, and without this the "Create Invoice" button just
// failed with a raw "Either accountId or contactId is required" error and no
// way to fix it without leaving the pipeline. Lets the user pick an existing
// Account/Contact, or quickly create a new Account inline, then generates the
// invoice with that link attached.
export default function GenerateInvoiceModal({ isOpen, onClose, dealId, onCreated }) {
  const dispatch = useDispatch();
  const [mode, setMode] = useState('account'); // 'account' | 'contact' | 'new_account'
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [contactId, setContactId] = useState('');
  const [newAccountName, setNewAccountName] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    Promise.all([
      dispatch(fetchAccounts({ limit: 200 })).unwrap(),
      dispatch(fetchContacts({ limit: 200 })).unwrap(),
    ])
      .then(([accountsRes, contactsRes]) => {
        setAccounts(accountsRes.accounts || accountsRes.data || []);
        setContacts(contactsRes.contacts || contactsRes.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isOpen, dispatch]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    let overrides = {};
    if (mode === 'account') {
      if (!accountId) return alertService.error('Error', 'Select an account first');
      overrides = { accountId };
    } else if (mode === 'contact') {
      if (!contactId) return alertService.error('Error', 'Select a contact first');
      overrides = { contactId };
    } else {
      if (!newAccountName.trim()) return alertService.error('Error', 'Enter an account name first');
      try {
        const account = await dispatch(createAccount({ name: newAccountName.trim() })).unwrap();
        overrides = { accountId: account._id };
      } catch (err) {
        return alertService.error('Error', err || 'Failed to create account');
      }
    }

    setSubmitting(true);
    try {
      const response = await api.post(`/crm/invoices/from-deal/${dealId}`, overrides);
      alertService.success('Success', `Invoice ${response.data.invoice.invoiceNumber} created`);
      onCreated?.(response.data.invoice);
      onClose();
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Failed to create invoice');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-md z-10 max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
        <div className="px-4 sm:px-6 py-3 border-b border-[#E5E5E5] flex items-center justify-between bg-white">
          <h3 className="text-[16px] font-bold text-[#2E2E2E] flex items-center gap-2"><Wallet size={16} className="text-[#0176D3]" /> Generate Invoice</h3>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#080707]"><X size={18} /></button>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          <p className="text-[13px] text-[#706E6B]">
            This deal isn't linked to an account or contact yet — invoices need at least one. Pick one below.
          </p>

          <div className="flex gap-2">
            {[
              { id: 'account', label: 'Existing Account' },
              { id: 'contact', label: 'Existing Contact' },
              { id: 'new_account', label: 'New Account' },
            ].map(opt => (
              <button
                key={opt.id}
                onClick={() => setMode(opt.id)}
                className={`flex-1 h-9 px-2 rounded text-[13px] font-semibold border ${mode === opt.id ? 'bg-[#0176D3] text-white border-[#0176D3]' : 'bg-[#F3F2F2] text-[#080707] border-[#D8DDE6] hover:bg-white'}`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {mode === 'account' && (
            <div className="space-y-1.5">
              <label className="text-[11px] font-semibold text-[#706E6B]">Account</label>
              <select
                value={accountId}
                onChange={e => setAccountId(e.target.value)}
                disabled={loading}
                className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
              >
                <option value="">{loading ? 'Loading…' : 'Select an account'}</option>
                {accounts.map(a => <option key={a._id} value={a._id}>{a.name}</option>)}
              </select>
            </div>
          )}

          {mode === 'contact' && (
            <div className="space-y-1.5">
              <label className="text-[11px] font-semibold text-[#706E6B]">Contact</label>
              <select
                value={contactId}
                onChange={e => setContactId(e.target.value)}
                disabled={loading}
                className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
              >
                <option value="">{loading ? 'Loading…' : 'Select a contact'}</option>
                {contacts.map(c => <option key={c._id} value={c._id}>{c.name || c.phoneNumber || c.email}</option>)}
              </select>
            </div>
          )}

          {mode === 'new_account' && (
            <div className="space-y-1.5">
              <label className="text-[11px] font-semibold text-[#706E6B]">Account Name</label>
              <input
                type="text"
                value={newAccountName}
                onChange={e => setNewAccountName(e.target.value)}
                placeholder="e.g. Acme Corp"
                className="w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3]"
              />
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[#D8DDE6] flex justify-end gap-2 bg-[#F4F6F9]">
          <button onClick={onClose} className="h-9 px-4 rounded text-[13px] font-semibold text-[#080707] hover:bg-white border border-transparent hover:border-[#D8DDE6] whitespace-nowrap shrink-0">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="h-9 px-4 bg-[#0176D3] text-white rounded text-[13px] font-semibold hover:bg-[#014486] disabled:opacity-50 whitespace-nowrap shrink-0"
          >
            {submitting ? 'Generating…' : 'Generate Invoice'}
          </button>
        </div>
      </div>
    </div>
  );
}
